import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useActivatePersonaApi,
  useAdoptPersonaApi,
  useDeactivatePersonaApi,
  useResetSitePersonaApi,
  useSitePersonaDetailApi,
  useSitePersonaHistoryApi,
  useSitePersonaListApi,
  useUpdateSitePersonaApi
} from '@/common/api/modules/persona'
import {
  PersonaConditionLeaf,
  PersonaDifficultyBand,
  PersonaMetricBrief,
  SitePersonaRow,
  SitePersonaTemplate
} from '@/common/api/params/persona'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import RadarChart from '@/components/view/atoms/chart/RadarChart'
import Heading from '@/components/view/atoms/heading/Heading'
import PersonaAvatar, { PersonaAvatarSize } from '@/components/view/atoms/image/PersonaAvatar'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import ConditionLeafEditor from '@/components/container/modules/persona/ConditionLeafEditor'
import DefinitionHistory from '@/components/container/modules/persona/DefinitionHistory'
import {
  hasEmptyValue,
  toEditableLeaves
} from '@/components/container/modules/persona/conditionLeaves'
import { AXIS_LABELS } from '@/components/container/page/site/persona/axis'

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

const BANDS: { key: PersonaDifficultyBand | 'all'; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: '1-3', label: '難易度1-3' },
  { key: '4-6', label: '難易度4-6' },
  { key: '7-8', label: '難易度7-8' },
  { key: '9-10', label: '難易度9-10' }
]

/** 該当件数の表示。null は「判定不能」で、0（該当者なし）とは意味が違う */
const MatchedSize = ({ value, judgeable }: { value: number | null; judgeable: boolean }) => {
  if (value === null) {
    return (
      <Text size={12} color={TextColor.SECONDARY} nowrap>
        {judgeable ? '未判定' : '判定不能'}
      </Text>
    )
  }
  return (
    <Text size={14} weight='bold' nowrap>
      {value.toLocaleString()} 人
    </Text>
  )
}

/**
 * 閾値の調整（8.13 / API は 7.12）。
 *
 * design.md「値と施策はサイトが持ち、意味と測り方はW2が持つ」の**値**の側だけを触る。
 * メトリクス（何を測るか）は増減させない。測り方をサイトごとに変えると横断比較が
 * 成立しなくなるため、ここに入口を作らない。
 */
const ConditionEditor = ({
  siteId,
  persona,
  metrics,
  onSaved
}: {
  siteId: number
  persona: SitePersonaRow
  metrics: PersonaMetricBrief[]
  onSaved: () => void
}) => {
  const initial = toEditableLeaves(persona.conditions)
  const [open, setOpen] = useState(false)
  const [leaves, setLeaves] = useState<PersonaConditionLeaf[]>(initial ?? [])
  const [saving, setSaving] = useState(false)

  const finish = () => {
    setSaving(false)
    setOpen(false)
    onSaved()
  }
  const { trigger: save } = useUpdateSitePersonaApi({
    onSuccess: finish,
    onError: () => setSaving(false)
  })
  const { trigger: reset } = useResetSitePersonaApi({
    onSuccess: finish,
    onError: () => setSaving(false)
  })

  if (initial === null) {
    return (
      <Text size={12} color={TextColor.SECONDARY}>
        この条件式は入れ子や範囲指定を含むため、画面からは調整できません
      </Text>
    )
  }

  const onSave = () => {
    setSaving(true)
    save({
      site_id: siteId,
      persona_id: persona.persona_id,
      conditions: { all: leaves }
    })
  }

  if (!open) {
    return (
      <div className='flex flex-wrap items-center gap-2'>
        <Button
          color={ButtonColor.SECONDARY}
          onClick={() => {
            setLeaves(initial)
            setOpen(true)
          }}
        >
          閾値を調整する
        </Button>
        {/* 改版があるときも同じ操作。カタログの値を丸ごと取り込む点は変わらない */}
        {(persona.customized || persona.has_new_version) && (
          <Button
            color={ButtonColor.SECONDARY}
            disabled={saving}
            loading={saving}
            onClick={() => {
              setSaving(true)
              reset({ site_id: siteId, persona_id: persona.persona_id })
            }}
          >
            {persona.has_new_version ? '新版を取り込む' : 'カタログの既定に戻す'}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className='border-border flex flex-col gap-3 rounded-lg border p-3'>
      <ConditionLeafEditor leaves={leaves} metrics={metrics} onChange={setLeaves} />

      <Text size={10} color={TextColor.TERTIARY}>
        保存しても該当件数はすぐには変わりません。次回の判定（毎日 07:10 JST）で反映されます。
      </Text>

      <div className='flex flex-wrap gap-2'>
        <Button disabled={saving || hasEmptyValue(leaves)} loading={saving} onClick={onSave}>
          保存
        </Button>
        <Button color={ButtonColor.SECONDARY} disabled={saving} onClick={() => setOpen(false)}>
          キャンセル
        </Button>
      </div>
    </div>
  )
}

/**
 * このサイトで行われた閾値変更の履歴。
 *
 * 他サイトの変更は含まれない（同じカタログを採用している別テナントが
 * どう調整したかは見せない）。閾値を戻したいときの手がかりになる。
 */
const SiteHistoryPanel = ({
  siteId,
  personaId,
  /** 保存のたびに変わる値。これが変わったら履歴を取り直す */
  revision
}: {
  siteId: number
  personaId: string
  revision: number
}) => {
  const [open, setOpen] = useState(false)
  const { data, mutate } = useSitePersonaHistoryApi(
    open ? { site_id: siteId, persona_id: personaId } : undefined
  )

  // 保存直後に開いたままだと古い履歴が残る。閾値を変えたら取り直す
  useEffect(() => {
    if (open) mutate()
  }, [revision]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) {
    return (
      <div className='mt-2'>
        <Button color={ButtonColor.SECONDARY} onClick={() => setOpen(true)}>
          変更履歴を見る
        </Button>
      </div>
    )
  }
  return (
    <div className='mt-2 flex flex-col gap-2'>
      <div className='flex items-center gap-2'>
        <Text size={12} weight='bold'>
          変更履歴
        </Text>
        <Button color={ButtonColor.SECONDARY} onClick={() => setOpen(false)}>
          閉じる
        </Button>
      </div>
      {data ? (
        <DefinitionHistory items={data.items} />
      ) : (
        <Text size={12} color={TextColor.SECONDARY}>
          読み込み中...
        </Text>
      )}
    </div>
  )
}

/** 採用前の確認ビュー（8.6）。載せるのは静的内容と群の傾向まで */
const DetailPanel = ({ siteId, templateId }: { siteId: number; templateId: string }) => {
  const { data, mutate } = useSitePersonaDetailApi({ site_id: siteId, template_id: templateId })
  // 保存のたびに進める。開いたままの履歴パネルに取り直させるために使う
  const [savedAt, setSavedAt] = useState(0)
  if (!data) return <Text color={TextColor.SECONDARY}>読み込み中...</Text>

  const t = data.template
  const sp = data.site_persona
  const hasRadar = data.radar.some((r) => r.score !== null)
  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-start gap-3'>
        <PersonaAvatar
          emoji={t.avatar_emoji}
          color={t.avatar_color}
          size={PersonaAvatarSize.LARGE}
          muted={!t.judgeable}
        />
        <div className='min-w-0'>
          <Text size={20} weight='bold'>
            {t.name}
          </Text>
          <div className='mt-1'>
            <Text size={12} color={TextColor.SECONDARY}>
              {t.template_id} ・ 難易度 {t.difficulty} ・{' '}
              {AXIS_LABELS[t.primary_axis ?? ''] ?? t.primary_axis ?? '—'}
            </Text>
          </div>
        </div>
      </div>

      <div>
        <Text size={12} color={TextColor.SECONDARY}>
          判定条件（原文）
        </Text>
        <div>
          <Text size={14}>{t.judge_text}</Text>
        </div>
      </div>

      <div>
        <Text size={12} color={TextColor.SECONDARY}>
          条件式
        </Text>
        {data.condition_summary.length > 0 ? (
          <ul className='mt-1 flex flex-col gap-0.5'>
            {data.condition_summary.map((c) => (
              <li key={c}>
                <Text size={12}>・{c}</Text>
              </li>
            ))}
          </ul>
        ) : (
          <div>
            <Text size={12} color={TextColor.SECONDARY}>
              条件式が未作成のため、このペルソナは判定の対象になりません
            </Text>
          </div>
        )}

        {sp && sp.has_new_version && (
          <div className='mt-1'>
            <Text size={10} color={TextColor.CAUTION}>
              新版あり（採用時 v{sp.template_version_adopted} → カタログ v{sp.template_version}
              ）。判定条件は自動では変わりません。「カタログの既定に戻す」
              を押したときだけ取り込まれます。
            </Text>
          </div>
        )}
        {sp && sp.customized && (
          <div className='mt-1'>
            <Text size={10} color={TextColor.TERTIARY}>
              このサイトで調整した値です（カタログの既定とは異なります）
            </Text>
          </div>
        )}
        {sp && sp.conditions_stale && (
          <div className='mt-1'>
            <Text size={10} color={TextColor.CAUTION}>
              条件を変更しました。該当 {sp.matched_size?.toLocaleString() ?? '—'} 人は変更前の
              件数で、次回の判定（毎日 07:10 JST）まで有効化できません。
            </Text>
          </div>
        )}
        {sp && sp.conditions && (
          <div className='mt-2'>
            <ConditionEditor
              siteId={siteId}
              persona={sp}
              metrics={data.metrics}
              onSaved={() => {
                mutate()
                setSavedAt(Date.now())
              }}
            />
          </div>
        )}
        {sp && <SiteHistoryPanel siteId={siteId} personaId={sp.persona_id} revision={savedAt} />}
      </div>

      {t.blocked_metrics.length > 0 && (
        <div>
          <Text size={12} color={TextColor.CRITICAL}>
            このサイトでは判定できません
          </Text>
          <div>
            <Text size={12} color={TextColor.SECONDARY}>
              算出できないメトリクス: {t.blocked_metrics.join(' / ')}
            </Text>
          </div>
          <div>
            <Text size={12} color={TextColor.SECONDARY}>
              該当者が0人という意味ではありません。必要なデータがまだありません。
            </Text>
          </div>
        </div>
      )}

      {t.tactics.length > 0 && (
        <div>
          <Text size={12} color={TextColor.SECONDARY}>
            施策例
          </Text>
          <div>
            <Text size={13}>{t.tactics.join(' / ')}</Text>
          </div>
        </div>
      )}

      <div>
        <Text size={12} color={TextColor.SECONDARY}>
          該当者の傾向（10軸・サイト内の相対値）
        </Text>
        {hasRadar ? (
          <RadarChart
            labels={data.radar.map((r) => AXIS_LABELS[r.axis] ?? r.axis)}
            series={[{ title: t.name, values: data.radar.map((r) => r.score) }]}
            height={300}
          />
        ) : (
          <div className='mt-1'>
            <Text size={12} color={TextColor.SECONDARY}>
              採用して判定を回すと、該当者の傾向が出ます
            </Text>
          </div>
        )}
      </div>
    </div>
  )
}

const PersonaLibraryPage = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)

  const [band, setBand] = useState<PersonaDifficultyBand | 'all'>('1-3')
  const [search, setSearch] = useState('')
  const [keyword, setKeyword] = useState('')
  const [onlyAdopted, setOnlyAdopted] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const detailRef = useRef<HTMLDivElement>(null)
  // 行ごとの操作。フックの isMutating を使うと全行が反応する
  const [acting, setActing] = useState<{ id: string; action: string } | null>(null)
  // 2026-10-04 追加：規模不足の強制有効化は、行内で確認してから送る
  const [confirmForce, setConfirmForce] = useState<string | null>(null)

  const { data, isLoading, mutate } = useSitePersonaListApi({
    site_id: siteId,
    ...(band !== 'all' && { difficulty_band: band }),
    ...(keyword && { search: keyword }),
    ...(onlyAdopted && { adopted: true })
  })

  const done = () => {
    setActing(null)
    setConfirmForce(null)
    mutate()
  }
  const fail = () => setActing(null)

  const { trigger: adopt } = useAdoptPersonaApi({ onSuccess: done, onError: fail })
  const { trigger: activate } = useActivatePersonaApi({ onSuccess: done, onError: fail })
  const { trigger: deactivate } = useDeactivatePersonaApi({ onSuccess: done, onError: fail })

  // 狭い画面（xl 未満）では確認パネルが一覧の下に回る。押したらパネルまで送る。
  // 広い画面は一覧の横で追従表示しているので、スクロールさせない
  useEffect(() => {
    if (!selected || window.matchMedia('(min-width: 1280px)').matches) return
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [selected])

  const isActing = (id: string, action: string) => acting?.id === id && acting.action === action

  const renderActions = (p: SitePersonaTemplate) => {
    if (!p.adopted) {
      return (
        <Button
          color={ButtonColor.PRIMARY}
          // 8.9 判定できないものは採用しても有効化できないので入口で止める
          disabled={!p.judgeable || acting?.id === p.template_id}
          loading={isActing(p.template_id, 'adopt')}
          onClick={() => {
            setActing({ id: p.template_id, action: 'adopt' })
            adopt({ site_id: siteId, template_id: p.template_id })
          }}
        >
          採用
        </Button>
      )
    }
    if (p.active) {
      return (
        <Button
          color={ButtonColor.SECONDARY}
          disabled={acting?.id === p.template_id}
          loading={isActing(p.template_id, 'deactivate')}
          onClick={() => {
            setActing({ id: p.template_id, action: 'deactivate' })
            deactivate({ site_id: siteId, persona_id: p.template_id })
          }}
        >
          停止
        </Button>
      )
    }
    // 2026-10-04 追加：該当が最小規模に満たないときは「強制で有効化」（force=true）を出す。
    //   API は従来から force を受けるが、画面からは送れなかった。判定不能（null）は force でも通さない
    const minSize = data?.min_active_size ?? 0
    const tooSmall = p.matched_size !== null && p.matched_size < minSize
    if (tooSmall) {
      if (confirmForce === p.template_id) {
        return (
          <div className='flex max-w-[260px] flex-col gap-1.5'>
            <Text size={10} color={TextColor.CAUTION}>
              該当 {p.matched_size} 人は最小規模 {minSize} 人に足りません。
              人数が少ないと効果を測れませんが、承知のうえで有効化しますか？（操作は監査ログに残ります）
            </Text>
            <div className='flex gap-2'>
              <Button
                color={ButtonColor.CRITICAL}
                disabled={acting?.id === p.template_id}
                loading={isActing(p.template_id, 'activate')}
                onClick={() => {
                  setActing({ id: p.template_id, action: 'activate' })
                  activate({ site_id: siteId, persona_id: p.template_id, force: true })
                }}
              >
                強制で有効化
              </Button>
              <Button
                color={ButtonColor.SECONDARY}
                disabled={acting?.id === p.template_id}
                onClick={() => setConfirmForce(null)}
              >
                やめる
              </Button>
            </div>
          </div>
        )
      }
      return (
        <div className='flex flex-col items-start gap-1'>
          <Button
            color={ButtonColor.SECONDARY}
            disabled={acting?.id === p.template_id}
            onClick={() => setConfirmForce(p.template_id)}
          >
            強制で有効化
          </Button>
          <Text size={10} color={TextColor.TERTIARY} nowrap>
            {minSize}人未満
          </Text>
        </div>
      )
    }
    return (
      <Button
        color={ButtonColor.PRIMARY}
        disabled={p.matched_size === null || acting?.id === p.template_id}
        loading={isActing(p.template_id, 'activate')}
        onClick={() => {
          setActing({ id: p.template_id, action: 'activate' })
          activate({ site_id: siteId, persona_id: p.template_id, force: false })
        }}
      >
        有効化
      </Button>
    )
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='ペルソナライブラリ'
        description='130選から採用します。採用すると判定の対象になり、有効化すると配信リストの同期が始まります。'
      />

      <Content>
        <div className='flex flex-col gap-4 xl:flex-row'>
          <div className='min-w-0 flex-1'>
            <Area>
              <AreaContent>
                <div className='mb-3 flex flex-wrap items-center gap-2'>
                  {BANDS.map((b) => (
                    <Button
                      key={b.key}
                      color={band === b.key ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                      onClick={() => setBand(b.key)}
                    >
                      {b.label}
                    </Button>
                  ))}
                  <Button
                    color={onlyAdopted ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                    onClick={() => setOnlyAdopted(!onlyAdopted)}
                  >
                    採用済みのみ
                  </Button>
                  <span className='flex-1' />
                  <input
                    className={textInput}
                    placeholder='名前・ID・判定条件'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setKeyword(search.trim())
                    }}
                  />
                  <Button color={ButtonColor.SECONDARY} onClick={() => setKeyword(search.trim())}>
                    検索
                  </Button>
                </div>

                {isLoading || !data ? (
                  <Text color={TextColor.SECONDARY}>読み込み中...</Text>
                ) : (
                  <>
                    <div className='mb-1'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {data.total} 体（採用 {data.adopted} / 有効 {data.active}）
                      </Text>
                    </div>
                    <Table>
                      <thead>
                        <TableRow head>
                          <TableHeadItem>
                            <Text size={12}>ペルソナ</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>難易度</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>該当</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>状態</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>操作</Text>
                          </TableHeadItem>
                        </TableRow>
                      </thead>
                      <tbody>
                        {data.items.map((p) => (
                          <TableRow key={p.template_id}>
                            <TableRowItem>
                              <button
                                type='button'
                                className='flex items-center gap-2 text-left'
                                onClick={() => setSelected(p.template_id)}
                              >
                                <PersonaAvatar
                                  emoji={p.avatar_emoji}
                                  color={p.avatar_color}
                                  muted={!p.judgeable}
                                />
                                <span>
                                  <Text size={14} color={TextColor.LINK}>
                                    {p.name}
                                  </Text>
                                  <span className='block'>
                                    <Text size={10} color={TextColor.TERTIARY}>
                                      {p.template_id}
                                    </Text>
                                  </span>
                                </span>
                              </button>
                            </TableRowItem>
                            <TableRowItem>
                              <Text size={12}>{p.difficulty}</Text>
                            </TableRowItem>
                            <TableRowItem>
                              <MatchedSize value={p.matched_size} judgeable={p.judgeable} />
                            </TableRowItem>
                            <TableRowItem>
                              {!p.judgeable ? (
                                <>
                                  <Text size={12} color={TextColor.SECONDARY} nowrap>
                                    判定不能
                                  </Text>
                                  <div>
                                    <Text size={10} color={TextColor.TERTIARY}>
                                      {p.blocked_metrics.length > 0
                                        ? `要: ${p.blocked_metrics.join(' / ')}`
                                        : '条件式が未作成'}
                                    </Text>
                                  </div>
                                </>
                              ) : p.active ? (
                                <Text size={12} color={TextColor.SUCCESS} nowrap>
                                  有効
                                </Text>
                              ) : p.adopted ? (
                                <Text size={12} color={TextColor.SECONDARY} nowrap>
                                  採用のみ
                                </Text>
                              ) : (
                                <Text size={12} color={TextColor.SECONDARY} nowrap>
                                  未採用
                                </Text>
                              )}
                              {p.has_new_version && (
                                <div className='mt-1'>
                                  <Text size={10} color={TextColor.CAUTION} nowrap>
                                    新版あり
                                  </Text>
                                </div>
                              )}
                            </TableRowItem>
                            <TableRowItem>{renderActions(p)}</TableRowItem>
                          </TableRow>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </AreaContent>
            </Area>
          </div>

          {/* 一覧の下の方を押しても上に戻らずに済むよう、広い画面では画面内に追従させる。
            top はヘッダーの高さ（57px）+ 余白。中身が画面より長いときはパネル内でスクロールする */}
          <div
            ref={detailRef}
            className='w-full scroll-mt-[73px] xl:sticky xl:top-[73px] xl:max-h-[calc(100vh-89px)] xl:w-[420px] xl:shrink-0 xl:self-start xl:overflow-y-auto'
          >
            <Area>
              <Heading subText='採用の判断に必要な内容だけを出します。効果実測や施策提案はここには置きません。'>
                確認
              </Heading>
              <AreaContent>
                {selected ? (
                  <DetailPanel siteId={siteId} templateId={selected} />
                ) : (
                  <Text size={13} color={TextColor.SECONDARY}>
                    一覧のペルソナ名を押すと、判定条件・施策例・該当者の傾向が出ます。
                  </Text>
                )}
              </AreaContent>
            </Area>
          </div>
        </div>
      </Content>
    </Main>
  )
}

export default PersonaLibraryPage
